import { skipToken, useQuery } from '@tanstack/react-query'

import { disableKeyWhenMissing } from '../../helpers/utilities.ts'
import {
  graphql_call as graphql_admin_call,
  graphql_multi_call as graphql_admin_multi_call,
} from '../../schemas/server_admin_query'

import { keys } from './keystore.ts'
import {
  query_admin_redeemable,
  query_admin_redeemables,
  query_admin_redemption,
  query_admin_redemptions,
  query_liquidity_providers,
} from './redeem_graphql.ts'
import type {
  AdminRedeemable,
  AdminRedemptionDetail,
  AdminRedemptionListItem,
  LiquidityProviderWithBuckets,
  RedemptionFilters,
} from './types.ts'

export type AdminRedeemablesResponse = {
  redeemables: {
    nodes: AdminRedeemable[]
    total_count: number
  }
}

export type AdminRedemptionsResponse = {
  redemptions: {
    nodes: AdminRedemptionListItem[]
    total_count: number
    pageInfo: {
      hasNextPage: boolean
      endCursor: string | null
    }
  }
}

export function useAdminRedeemables() {
  return useQuery({
    queryKey: keys.admin_redeemables.all.queryKey,
    queryFn: async (): Promise<AdminRedeemablesResponse | null> => {
      try {
        const response = await graphql_admin_multi_call({
          query: query_admin_redeemables,
          variables: {},
        })
        return response as AdminRedeemablesResponse
      } catch (error) {
        console.error('Error fetching redeemables:', error)
        return null
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })
}

export function useAdminRedeemable(id: string) {
  return useQuery({
    queryKey: keys.admin_redeemables.detail(disableKeyWhenMissing(id)).queryKey,
    queryFn: !id
      ? skipToken
      : async (): Promise<AdminRedeemable | undefined> => {
          try {
            const response = await graphql_admin_call({
              query: query_admin_redeemable,
              variables: { id },
            })
            if (!response) return undefined
            return response as AdminRedeemable
          } catch (error) {
            console.error('Error fetching redeemable:', error)
            return undefined
          }
        },
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    enabled: Boolean(id),
  })
}

export function useAdminRedemptions(filters: RedemptionFilters) {
  return useQuery({
    queryKey: keys.admin_redemptions.list(filters).queryKey,
    // eslint-disable-next-line complexity
    queryFn: async (): Promise<AdminRedemptionsResponse | null> => {
      try {
        const response = await graphql_admin_multi_call({
          query: query_admin_redemptions,
          variables: {
            first: filters.first ?? 50,
            ...(filters.after && { after: filters.after }),
            ...(filters.redeemable_name && {
              redeemable_name: filters.redeemable_name,
            }),
            ...(filters.status && { status: filters.status }),
            ...(filters.settled_start_date && {
              settled_start_date: filters.settled_start_date,
            }),
            ...(filters.settled_end_date && {
              settled_end_date: filters.settled_end_date,
            }),
          },
        })
        return response as AdminRedemptionsResponse
      } catch (error) {
        console.error('Error fetching redemptions:', error)
        return null
      }
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
  })
}

export function useAdminRedemption(id: string) {
  return useQuery({
    queryKey: keys.admin_redemptions.detail(disableKeyWhenMissing(id)).queryKey,
    queryFn: !id
      ? skipToken
      : async (): Promise<AdminRedemptionDetail | undefined> => {
          try {
            const response = await graphql_admin_call({
              query: query_admin_redemption,
              variables: { id },
            })
            if (!response) return undefined
            return response as AdminRedemptionDetail
          } catch (error) {
            console.error('Error fetching redemption:', error)
            return undefined
          }
        },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: Boolean(id),
  })
}

export type LiquidityProvidersResponse = {
  liquidity_providers: {
    nodes: LiquidityProviderWithBuckets[]
    total_count: number
  }
}

export function useLiquidityProviders() {
  return useQuery({
    queryKey: keys.liquidity_providers.all.queryKey,
    queryFn: async (): Promise<LiquidityProvidersResponse | null> => {
      try {
        const response = await graphql_admin_multi_call({
          query: query_liquidity_providers,
          variables: {},
        })
        return response as LiquidityProvidersResponse
      } catch (error) {
        console.error('Error fetching liquidity providers:', error)
        return null
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })
}
